// In-network plans per clinician — the single source of truth for every
// published plan list (see src/data/insurance.js and src/tests/insurance.test.js).
// Confirmed by Cameron 2026-10-14. Excellus, Highmark and out-of-state Blue
// (BlueCard) plans are covered by everyone who takes Anthem / Empire.
//
// MHC-LP supervisees cannot accept NYCE PPO, United HealthCare, or Optum
// for billing reasons — these networks must only appear on the lists of
// fully licensed clinicians.
export const mhcLpInsuranceAccepted = [
  'Aetna',
  'Anthem / Empire BlueCross',
  'Excellus BCBS',
  'Highmark BCBS of NY',
  'Out-of-state BCBS (BlueCard)',
  'Carelon',
  'Cigna',
  'EmblemHealth',
  'MagnaCare',
  'Meritain',
  'NYSHIP / The Empire Plan',
];

// Ryn's participation matches the supervised clinicians' list (confirmed
// 2026-10-14) unless and until additional payer panels are confirmed.
export const rynInsuranceAccepted = [...mhcLpInsuranceAccepted];

// Not in-network, but Ryn can see clients on these plans who have
// out-of-network benefits: Ryn bills out-of-network and the client pays their
// in-network copay (confirmed by Cameron 2026-10-14). Matching treats these
// like accepted plans; published copy always says "out-of-network benefits".
export const rynOutOfNetworkAccess = ['United HealthCare / Optum', 'Oxford', 'Oscar', 'UMR'];

// UnitedHealthCare-family plans (Oxford, Oscar, UMR) are Cameron only.
export const cameronInsuranceAccepted = [
  ...mhcLpInsuranceAccepted,
  'NYCE PPO',
  'United HealthCare / Optum',
  'Oxford',
  'Oscar',
  'UMR',
];

export const clarkInsuranceAccepted = ['Carelon', 'Cigna'];

export const commonInsuranceAccepted = mhcLpInsuranceAccepted;

const commonInsuranceNote = 'Coverage varies by plan and clinician.';
const onlineAcrossNewYork = 'Online across New York';

export const clinicianProfiles = {
  'cameron-eshgh': {
    slug: 'cameron-eshgh',
    preferredTherapistValue: 'Cameron Eshgh',
    seoTitle: 'Cameron Eshgh, LMHC-D | Relational & EMDR Therapist in New York',
    seoDescription:
      'Cameron Eshgh, LMHC-D, offers relational, trauma-informed therapy and EMDR in New York for adults and couples who understand themselves and still feel stuck.',
    fullName: 'Cameron Eshgh, LMHC-D',
    firstName: 'Cameron',
    phone: '+1-212-219-0222',
    dateModified: '2026-10-14',
    pronouns: 'All / any',
    roleLabel: 'Founder & Psychotherapist | Relational Therapy for Adults & Couples',
    listingRole: 'Founder & Psychotherapist',
    listingSummary: 'Relational therapy for adults and couples seeking deeper healing and meaningful change.',
    fitStatement:
      'A relational, depth-oriented therapist for people who understand themselves well and still feel stuck — who want change that is lived, not just understood.',
    goodFitFor: [
      'Insight that has not become change',
      'Attachment & relational patterns',
      'Trauma & nervous-system stress',
      'Perfectionism & high-achiever stress',
      'Identity, meaning & self-trust',
      'Couples',
    ],
    therapyStyle: ['Warm', 'Depth-oriented', 'Relationally engaged', 'Integrative'],
    image: '/images/clinicians/cameron-profile.webp',
    availabilityStatus: 'Currently full — accepting waitlist requests',
    acceptingNewClients: false,
    insuranceAccepted: cameronInsuranceAccepted,
    insuranceNote: commonInsuranceNote,
    bestFitConcerns: ['anxiety', 'burnout', 'perfectionism', 'attachment wounds', 'relationship patterns'],
    worksWith: ['adults', 'couples', 'reflective clients', 'relationship pattern clients'],
    languages: ['English'],
    sessionFormat: 'Online across New York, New Jersey, Massachusetts, Florida, and Vermont',
    schema: {
      // The only clinician licensed outside New York (see Practical details).
      areaServedStates: ['New York', 'New Jersey', 'Massachusetts', 'Florida', 'Vermont'],
      jobTitle: 'Founder and Psychotherapist',
      credentials: [{ name: 'LMHC-D', category: 'license' }],
      // Cameron's individual Psychology Today counselor profile (965745).
      // The practice's group profile (1777198) lives in PRACTICE_SAME_AS.
      sameAs: [
        'https://www.psychologytoday.com/us/therapists/peace-love-wellness-new-york-ny/965745',
        'https://mentalhealthmatch.com/therapist-profile/new-york/cameron-eshgh',
        'https://www.therapyden.com/therapist/cameron-eshgh-new-york-ny',
        'https://www.inclusivetherapists.com/new-york/new-york/cameron-eshgh',
        'https://nqttcn.com/listing/cameron-eshgh/',
        'https://www.manhattanalternative.com/team/cameron-eshgh/',
      ],
      // License numbers as published in the Practical details section below.
      identifier: [
        { propertyID: 'NY LMHC-D License', value: 'D012254' },
        { propertyID: 'NJ LPC License', value: '37PC01218200' },
        { propertyID: 'MA LMHC License', value: 'LMHC10005097' },
        { propertyID: 'FL LMHC License', value: 'MH27361' },
        { propertyID: 'VT LCMHC Telehealth License', value: '068.0136500TELE' },
      ],
      alumniOf: [
        { name: 'Concordia University Irvine' },
        {
          name: 'Columbia School of Social Work',
          parentOrganization: 'Columbia University',
        },
        { name: 'One Spirit Learning Alliance' },
        { name: 'University of Virginia' },
      ],
      knowsAbout: [
        'Relational therapy',
        'Trauma-informed therapy',
        'EMDR therapy',
        'Eye movement desensitization and reprocessing',
        'Dialectical behavior therapy',
        'IFS-informed support',
        'Adults',
        'Couples and relationship therapy',
        'Anxiety',
        'Burnout',
        'Perfectionism',
        'High-achiever stress',
        'Attachment wounds',
        'Substance use recovery',
        'Serious mental illness',
        'Psychosis',
        'Co-occurring concerns',
        'Identity and meaning',
        'Self-trust',
      ],
    },
    intro: [
      'Cameron Eshgh is the founder of Peace Love Wellness and a psychotherapist offering relational, trauma-informed therapy for adults and couples across New York.',
      'Cameron may be a strong fit if you understand yourself well and still feel stuck — and want change that is lived and felt, not just understood.',
    ],
    heroSecondary: { label: 'Our Approach', href: '/our-approach' },
    sections: [
      {
        title: 'Who Cameron works well with',
        paragraphs: [
          'Cameron works with adults and couples who are reflective and capable on the outside while privately carrying a great deal.',
          'Many have done therapy before and are looking for something deeper, more relational, and more integrating than what they have had.',
        ],
        bulletIntro: 'Common threads include:',
        bullets: [
          'anxiety and overwhelm',
          'perfectionism and high-achiever stress',
          'trauma and attachment wounds',
          'relationship strain',
          'questions of identity and meaning',
          'long-standing patterns that no longer fit',
        ],
      },
      {
        title: 'What therapy with Cameron feels like',
        theme: 'dark',
        paragraphs: [
          'Therapy with Cameron is warm, depth-oriented, and relationally engaged.',
          'Cameron pays attention not only to what you say but to what is happening underneath — the patterns, the protective strategies, the places you brace. The process is honest without being harsh, and steady enough to stay with what is hard.',
        ],
      },
      {
        title: 'Identity, complexity, and affirmation',
        paragraphs: [
          'Cameron offers care that honors complexity rather than reducing anyone to a diagnosis, label, or single part of their story.',
          'His approach is often a good fit for people who move between worlds, carry more than others see, or are trying to understand themselves across many layers of identity, relationship, culture, meaning, and lived experience.',
        ],
      },
      {
        title: 'Depth of clinical experience',
        theme: 'dark',
        paragraphs: [
          "Cameron's background includes several years of work in outpatient substance use and a year supporting people living with serious mental illness, including psychosis.",
          'That experience informs his comfort with complexity — including co-occurring concerns, recovery, and high-stakes emotional terrain — and his steadiness with material other settings can find hard to hold.',
        ],
      },
      {
        title: "Cameron's approach",
        paragraphs: [
          "Cameron's work is relational, trauma-informed, and integrative. He attends to attachment and relational patterns, trauma responses and nervous-system stress, anxiety and emotional blocks, identity and self-trust, and the move from insight toward change that feels lived.",
          'He is also trained in EMDR — a structured, trauma-focused approach that works with how distressing memories are stored rather than only how they are discussed. He draws on it when understanding something has not been enough to change it, and he is direct about when a slower pace or a different approach would serve you better.',
          'He is DBT-trained and brings dialectical behavior therapy skills into individual work — emotion regulation, distress tolerance, and interpersonal effectiveness — for people whose emotions arrive faster than they can think. His work is also informed by Internal Family Systems.',
          'He also helps shape the practice through supervision and mentorship.',
        ],
      },
      {
        title: 'Practical details',
        theme: 'dark',
        bullets: [
          'LMHC-D, fully licensed — NY D012254 · NJ 37PC01218200 · MA LMHC10005097 · FL MH27361 · VT 068.0136500TELE',
          'trained in EMDR — basic training complete',
          'DBT-trained — DBT skills within individual therapy',
          'IFS-informed',
          'individuals and couples',
          'online across New York, New Jersey, Massachusetts, Florida, and Vermont (the only clinician licensed outside New York)',
          'insurance accepted',
        ],
        badges: [
          {
            type: 'pt-seal',
            href: 'https://www.psychologytoday.com/profile/965745',
            alt: 'Verified on Psychology Today',
            badge: '13',
            profileId: '965745',
            code: 'aHR0cHM6Ly93d3cucHN5Y2hvbG9neXRvZGF5LmNvbS9hcGkvdmVyaWZpZWQtc2VhbC9zZWFscy8xMy9wcm9maWxlLzk2NTc0NT9jYWxsYmFjaz1zeGNhbGxiYWNr',
          },
          { href: 'https://mentalhealthmatch.com/therapist-profile/new-york/cameron-eshgh', src: '/images/mental-health-match-badge.svg', alt: 'Mental Health Match profile' },
          { href: 'https://www.therapyden.com/therapist/cameron-eshgh-new-york-ny', src: 'https://s3-us-west-2.amazonaws.com/therapyden1/logos/therapyden-web-badge.png', alt: 'Proud Member of TherapyDen' },
          { href: 'https://www.inclusivetherapists.com/new-york/new-york/cameron-eshgh?from=badge', src: 'https://www.inclusivetherapists.com/images/inclusive_therapists_members_badge.png', alt: 'Inclusive Therapists member' },
          { href: 'https://nqttcn.com/listing/cameron-eshgh/', src: '/images/nqttcn-badge.png', alt: 'NQTTCN member' },
          { href: 'https://www.manhattanalternative.com/team/cameron-eshgh/', src: '/images/manhattan-alternative-badge.png', alt: 'Manhattan Alternative member' },
        ],
      },
    ],
    howToGetStartedText:
      'We help match clients based on fit, insurance, availability, and the kind of support they are seeking.',
    howToGetStartedLinks: [
      { label: 'Insurance & Fees', href: '/insurance-fees' },
      { label: 'Coaching & Spiritual Counseling', href: '/coaching-and-spiritual-counseling' },
      { label: 'Find Your Therapist', href: '/our-clinicians' },
    ],
    finalText:
      'If you are looking for therapy in New York that feels relational, trauma-informed, identity-affirming, and grounded in meaningful change, Cameron may be a strong fit.',
    finalSecondary: { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
  },
  'ryn-cagle': {
    slug: 'ryn-cagle',
    preferredTherapistValue: 'Ryn Cagle',
    seoTitle: 'Ryn Cagle, LMHC | Affirming Online Therapy in New York',
    seoDescription:
      'Ryn Cagle, LMHC, offers affirming online therapy in New York for neurodivergent, queer, trans, chronically ill, and disabled clients.',
    fullName: 'Ryn Cagle, LMHC',
    firstName: 'Ryn',
    pronouns: 'They / them / theirs',
    roleLabel: 'Licensed Mental Health Counselor (LMHC) | Affirming Online Therapy',
    listingRole: 'Affirming Online Therapy',
    listingSummary: 'Affirming online therapy for ADHD, autistic, neurodivergent, queer, trans, chronically ill, and disabled clients.',
    fitStatement:
      'Especially good with people who have spent a long time feeling not-quite-understood — by past therapists, by systems, or by the people around them.',
    goodFitFor: [
      'ADHD, autistic & neurodivergent adults',
      'Queer, trans & gender-diverse clients',
      'Chronic illness & disability',
      'Health anxiety & medical conditions',
      'Religious trauma',
    ],
    therapyStyle: ['Supportive', 'Transparent', 'Collaborative', 'Gently challenging'],
    image: '/images/clinicians/ryn-profile.webp',
    availabilityStatus: 'Currently accepting new clients',
    acceptingNewClients: true,
    insuranceAccepted: rynInsuranceAccepted,
    outOfNetworkAccess: rynOutOfNetworkAccess,
    insuranceNote: commonInsuranceNote,
    bestFitConcerns: ['identity', 'ADHD', 'autism', 'neurodivergence', 'chronic illness', 'religious trauma'],
    worksWith: ['adults', 'ADHD clients', 'autistic clients', 'queer and trans clients', 'neurodivergent clients', 'chronically ill and disabled clients'],
    languages: ['English'],
    sessionFormat: onlineAcrossNewYork,
    schema: {
      jobTitle: 'Licensed Mental Health Counselor',
      credentials: [{ name: 'LMHC', category: 'license' }],
      sameAs: [
        'https://www.psychologytoday.com/us/therapists/ryn-cagle-new-york-ny/1802481',
      ],
      alumniOf: [
        {
          name: 'Teachers College',
          parentOrganization: 'Columbia University',
        },
      ],
      knowsAbout: [
        'Affirming therapy',
        'ADHD',
        'Autism',
        'Queer and trans clients',
        'Neurodivergence',
        'Chronic illness and disability',
        'Health anxiety',
        'Religious trauma',
        'Cult recovery',
      ],
    },
    intro: [
      'Ryn Cagle offers virtual therapy across New York rooted in warmth, honesty, and deep respect for the complexity of lived experience.',
      'Ryn may be a strong fit if you have spent a long time feeling not-quite-understood — by past therapists, by systems, or by the people around you.',
    ],
    heroSecondary: { label: 'Find Your Therapist', href: '/our-clinicians' },
    sections: [
      {
        title: 'Who Ryn works well with',
        bullets: [
          'ADHD, autistic, and neurodivergent adults',
          'Queer, trans, and gender-diverse clients',
          'Chronically ill and disabled clients',
          'People navigating health anxiety or the emotional weight of a medical condition',
          'People healing from religious trauma or cult involvement',
          'Clients carrying complex or overlapping diagnoses',
        ],
      },
      {
        title: 'What therapy with Ryn feels like',
        theme: 'dark',
        paragraphs: [
          'Therapy with Ryn is supportive, transparent, collaborative, and gently challenging.',
          'Ryn works at a pace that honors your capacity and consent — care that can flex around fluctuating energy, chronic symptoms, and demanding weeks, rather than asking you to push through.',
        ],
      },
      {
        title: 'Lived experience and resonance',
        paragraphs: [
          'Ryn brings lived understanding of disability, neurodivergence, and nonbinary identity into their care.',
          'Their approach is socially informed — attentive to how systems, marginalization, and isolation shape distress, not only the individual in isolation.',
        ],
      },
      {
        title: "Ryn's approach",
        theme: 'dark',
        paragraphs: [
          'Ryn works integratively and collaboratively, drawing from multiple approaches depending on what you need rather than imposing a single method.',
          'Therapy with Ryn is meant to feel accessible and human — a place to build safety, deepen self-understanding, and move toward a life that feels more livable and more your own.',
        ],
      },
      {
        title: 'Practical details',
        bullets: [
          'LMHC, fully licensed clinician',
          'individuals and couples',
          'online across New York',
          'insurance accepted',
        ],
      },
    ],
    howToGetStartedText:
      'We help match clients based on fit, current availability, insurance, and the type of support that feels most aligned.',
    howToGetStartedLinks: [
      { label: 'Insurance & Fees', href: '/insurance-fees' },
      { label: 'Our Approach', href: '/our-approach' },
    ],
    finalText:
      'If you are looking for affirming online therapy in New York that is collaborative, accessible, and grounded in real respect for lived experience, Ryn may be a strong fit.',
    finalSecondary: { label: 'Find Your Therapist', href: '/our-clinicians' },
  },
  'clark-sun': {
    slug: 'clark-sun',
    preferredTherapistValue: 'Clark Sun',
    seoTitle: 'Clark Sun, LCAT | Trauma-Informed Therapist in New York',
    seoDescription:
      'Clark Sun, LCAT, offers trauma-informed, culturally responsive therapy in New York for identity, burnout, attachment, and deeper emotional healing.',
    fullName: 'Clark Sun, LCAT',
    firstName: 'Clark',
    pronouns: 'He / they',
    roleLabel: 'Licensed Creative Arts Therapist | Attachment, Identity & Burnout',
    listingRole: 'Attachment, Identity & Burnout',
    listingSummary: 'Trauma-informed, culturally responsive care for identity exploration, burnout, and deeper emotional healing.',
    fitStatement:
      'A trauma-informed, culturally responsive therapist for people who have spent much of their lives showing up for others and are learning to show up for themselves.',
    goodFitFor: [
      'Identity exploration & cultural transitions',
      'Black and LGBTQIA2S+ clients',
      'Grief & loss',
      'Burnout in helping professionals',
      'Anger & emotional regulation',
      'Perfectionism & high-achiever stress',
      'Attachment & self-worth',
    ],
    therapyStyle: ['Intentional', 'Grounded', 'Collaborative', 'Embodied'],
    image: '/images/clinicians/clark-profile.webp',
    availabilityStatus: 'Currently accepting new clients',
    acceptingNewClients: true,
    insuranceAccepted: clarkInsuranceAccepted,
    insuranceNote: commonInsuranceNote,
    bestFitConcerns: ['identity exploration', 'trauma', 'burnout', 'grief and loss', 'anger and emotional regulation', 'attachment wounds'],
    worksWith: ['adults', 'Black clients', 'LGBTQIA2S+ clients', 'gender-diverse clients'],
    languages: ['English'],
    sessionFormat: onlineAcrossNewYork,
    schema: {
      jobTitle: 'Licensed Creative Arts Therapist',
      credentials: [{ name: 'LCAT', category: 'license' }],
      alumniOf: [{ name: 'New York University' }],
      sameAs: [
        'https://www.psychologytoday.com/us/therapists/clark-sun-new-york-ny/1114528',
        'https://www.instagram.com/clarkxsole/',
        'https://www.linkedin.com/in/brandon-clark-sun',
        'https://clarksuntherapy.com',
      ],
      knowsAbout: [
        'Identity exploration',
        'Trauma',
        'Burnout',
        'Grief and loss',
        'Anger',
        'Emotional regulation',
        'Perfectionism',
        'High-achiever stress',
        'Attachment wounds',
        'Culturally responsive care',
        'Creative arts therapy',
      ],
    },
    intro: [
      'Clark Sun offers trauma-informed, relational therapy for adults across New York who are exploring identity, healing emotional pain, and learning how to feel more at home in themselves and their relationships.',
      'Clark may be a strong fit if you have spent much of your life showing up for others and are learning how to show up for yourself.',
    ],
    heroSecondary: { label: 'Find Your Therapist', href: '/our-clinicians' },
    sections: [
      {
        title: 'Who Clark works well with',
        bullets: [
          'Black, LGBTQIA2S+, and gender-diverse clients',
          'Anxiety, depression, trauma, and PTSD',
          'People moving through grief and loss',
          'Burnout in healthcare workers, caregivers, and helping professionals',
          'Identity shifts and cultural transitions',
          'Perfectionism and high-achiever stress',
          'Anger and emotional regulation',
          'Attachment wounds, self-worth, and relational patterns',
        ],
      },
      {
        title: 'What therapy with Clark feels like',
        theme: 'dark',
        paragraphs: [
          'Therapy with Clark is intentional, grounded, collaborative, and embodied — with space for both practical support and deeper emotional healing.',
          'Clark pays attention to what your body and emotions have learned to carry, and moves at a pace that supports real steadiness.',
        ],
      },
      {
        title: 'Cultural responsiveness and affirmation',
        paragraphs: [
          "Clark's practice is rooted in affirming, culturally responsive care, with deep respect for identity, community belonging, and the search for a more self-defined life.",
        ],
      },
      {
        title: "Clark's approach",
        theme: 'dark',
        paragraphs: [
          'Clark works integratively and embodied, drawing from DBT-informed skill-building, somatic and attachment-informed approaches, mindfulness and values-based practices, and creative-arts interventions when useful.',
          'His approach is grounded within relational, culturally responsive care.',
        ],
      },
      {
        title: 'Practical details',
        bullets: [
          'LCAT, fully licensed',
          'individuals and couples',
          'online across New York',
          'insurance accepted',
        ],
      },
    ],
    howToGetStartedText:
      'We help match clients based on fit, insurance, availability, and the type of support they are seeking.',
    howToGetStartedLinks: [
      { label: 'Insurance & Fees', href: '/insurance-fees' },
      { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
    ],
    finalText:
      'If you are looking for therapy in New York that is affirming, trauma-informed, relational, and deeper than surface-level coping, Clark may be a strong fit.',
    finalSecondary: { label: 'Find Your Therapist', href: '/our-clinicians' },
  },
  'zaire-smalls': {
    slug: 'zaire-smalls',
    preferredTherapistValue: 'Zaire Smalls',
    seoTitle: 'Zaire Smalls, MHC-LP | Virtual Therapist in New York',
    seoDescription:
      'Zaire Smalls, MHC-LP, offers culturally responsive online therapy in New York for anxiety, ADHD, trauma, identity, and life transitions.',
    fullName: 'Zaire Smalls, MHC-LP',
    firstName: 'Zaire',
    pronouns: 'She / her / hers',
    roleLabel: 'Virtual Therapist | Culturally Responsive Support for Anxiety, Trauma & Identity',
    listingRole: 'Culturally Responsive Therapist',
    listingSummary: 'Culturally responsive support for anxiety, ADHD, depression, trauma, identity, and major life transitions.',
    fitStatement:
      'A collaborative, culturally responsive therapist who centers the ways culture, family, identity, and community shape emotional wellbeing.',
    goodFitFor: [
      'Culture, family & identity',
      'Anxiety, panic & ADHD',
      'Trauma & self-criticism',
      'Anger & emotional regulation',
      'Identity development',
      'Life transitions',
    ],
    therapyStyle: ['Warm', 'Collaborative', 'Culturally responsive', 'Authentic'],
    image: '/images/clinicians/zaire-profile.webp',
    availabilityStatus: 'Currently accepting new clients',
    acceptingNewClients: true,
    insuranceAccepted: mhcLpInsuranceAccepted,
    insuranceNote: commonInsuranceNote,
    bestFitConcerns: ['anxiety', 'panic attacks', 'ADHD', 'depression', 'trauma', 'anger and emotional regulation', 'life transitions'],
    worksWith: ['individuals', 'couples', 'clients exploring identity', 'online clients'],
    languages: ['English'],
    sessionFormat: onlineAcrossNewYork,
    schema: {
      jobTitle: 'Limited Permit Mental Health Counselor',
      credentials: [{ name: 'MHC-LP', category: 'limited permit' }],
      alumniOf: [
        { name: 'Southern New Hampshire University' },
        { name: 'Seton Hall University' },
      ],
      sameAs: ['https://zairesmallstherapy.com/'],
      knowsAbout: [
        'Anxiety',
        'Panic attacks',
        'ADHD',
        'Depression',
        'Trauma',
        'Trauma-informed therapy',
        'Anger',
        'Emotional regulation',
        'Self-esteem',
        'Self-criticism',
        'Identity development',
        'Life transitions',
        'Relationship stress',
        'Culturally responsive therapy',
        'Online therapy',
        'CBT',
        'Relational Cultural Theory',
        'Solution-Focused Therapy',
        'Adlerian therapy',
      ],
    },
    intro: [
      'Zaire Smalls offers virtual therapy across New York for individuals and couples seeking support that is collaborative, culturally responsive, and grounded in real respect for lived experience.',
      'Zaire may be a strong fit if you want a collaborative therapist who understands that healing happens in context — shaped by culture, family, identity, and community, not in isolation.',
    ],
    heroSecondary: { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
    sections: [
      {
        title: 'Who Zaire works well with',
        paragraphs: [
          'Zaire works with individuals and couples feeling overwhelmed or emotionally stretched, uncertain about who they are becoming, or ready to understand long-standing patterns.',
        ],
        bulletIntro: 'Common reasons people come to her include:',
        bullets: [
          'Anxiety and stress',
          'Panic and panic attacks',
          'ADHD and focus-related challenges',
          'Depression',
          'Trauma',
          'Anger and emotional regulation',
          'Self-esteem and self-criticism',
          'Identity development',
          'Life transitions',
        ],
      },
      {
        title: 'What therapy with Zaire feels like',
        theme: 'dark',
        paragraphs: [
          'Therapy with Zaire is warm, collaborative, and authentic.',
          'Zaire tailors the process to you rather than running a one-size-fits-all model, so therapy feels relevant to the realities of your life.',
        ],
      },
      {
        title: 'Cultural responsiveness',
        paragraphs: [
          "A defining part of Zaire's approach is her culturally responsive lens — centering the ways culture, family, identity, and community shape emotional wellbeing, so care feels more relevant, more affirming, and more connected to your actual life.",
        ],
      },
      {
        title: "Zaire's approach",
        theme: 'dark',
        paragraphs: [
          'Zaire works integratively and research-informed, drawing as needed from trauma-informed care, mindfulness, insight-oriented exploration, CBT, and strengths-based support.',
        ],
      },
      {
        title: 'Practical details',
        bullets: [
          'MHC-LP, supervised clinician',
          'individuals and couples',
          'online across New York',
          'insurance accepted',
        ],
      },
    ],
    howToGetStartedText: 'We help match clients based on needs, insurance, availability, and overall fit.',
    howToGetStartedLinks: [
      { label: 'Insurance & Fees', href: '/insurance-fees' },
      { label: 'Find Your Therapist', href: '/our-clinicians' },
    ],
    finalText:
      'If you are looking for virtual therapy in New York that is collaborative, culturally responsive, and grounded in meaningful support, Zaire may be a strong fit.',
    finalSecondary: { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
  },
  'michelle-batista': {
    slug: 'michelle-batista',
    preferredTherapistValue: 'Michelle Batista',
    seoTitle: 'Michelle Batista, MHC-LP | Bilingual Therapist in New York',
    seoDescription:
      'Michelle Batista, MHC-LP, offers bilingual online therapy in New York in English and Spanish for anxiety, overwhelm, self-doubt, and life stress.',
    fullName: 'Michelle Batista, MHC-LP',
    firstName: 'Michelle',
    dateModified: '2026-10-14',
    pronouns: 'She / her / hers',
    roleLabel: 'Bilingual Therapist | English & Spanish Support for Overwhelm and Emotional Regulation',
    listingRole: 'Bilingual Therapist',
    listingSummary: 'Bilingual therapy in English and Spanish for anxiety, overwhelm, emotional dysregulation, self-doubt, and life stress.',
    fitStatement:
      'A warm, steady therapist for people carrying overwhelm and emotional exhaustion, and for clients who want care in the language that feels most natural.',
    goodFitFor: [
      'Spanish-speaking clients',
      'Bicultural identity & family expectations',
      'Anxiety, panic & overwhelm',
      'Grief & loss',
      'Insight plus practical tools',
      'Feeling stuck despite real effort',
    ],
    therapyStyle: ['Warm', 'Engaged', 'Steady', 'Collaborative'],
    image: '/images/clinicians/michelle-profile.webp',
    availabilityStatus: 'Currently full — accepting waitlist requests',
    acceptingNewClients: false,
    insuranceAccepted: mhcLpInsuranceAccepted,
    insuranceNote: commonInsuranceNote,
    bestFitConcerns: ['anxiety', 'panic', 'overwhelm', 'grief and loss', 'emotional dysregulation', 'self-doubt', 'life stress', 'feeling stuck'],
    worksWith: ['adults', 'Spanish-speaking clients', 'clients wanting insight and tools'],
    languages: ['English', 'Spanish'],
    sessionFormat: onlineAcrossNewYork,
    schema: {
      jobTitle: 'Limited Permit Mental Health Counselor',
      credentials: [{ name: 'MHC-LP', category: 'limited permit' }],
      alumniOf: [
        { name: 'Medaille College' },
        { name: 'University at Buffalo', alternateName: 'University of Buffalo' },
      ],
      sameAs: [
        'https://www.psychologytoday.com/us/therapists/michelle-batista-babylon-ny/1691019',
        'https://www.linkedin.com/in/michelle-batista-5a66a2246/',
      ],
      knowsAbout: [
        'Bilingual therapy',
        'Anxiety',
        'Panic attacks',
        'Grief and loss',
        'Emotional dysregulation',
        'Overwhelm',
        'Self-doubt',
        'Bicultural identity',
        'Family expectations',
        'Client-centered therapy',
        'CBT-informed support',
        'English-speaking clients',
        'Spanish-speaking clients',
      ],
      knowsLanguage: ['English', 'Spanish'],
    },
    intro: [
      'Michelle Batista offers warm, empathic online therapy across New York for clients seeking support that feels human, collaborative, and steady.',
      'Michelle may be a strong fit if you are carrying overwhelm or emotional exhaustion — or if care in the language that feels most natural would help you feel more fully yourself.',
    ],
    heroSecondary: { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
    sections: [
      {
        title: 'Who Michelle works well with',
        bullets: [
          'Clients navigating overwhelm, anxiety, panic, or self-doubt',
          'Clients carrying sadness, grief and loss, emotional dysregulation, or the lingering impact of past experiences',
          'People experiencing uncertainty that feels too heavy to carry alone',
          'People who want a blend of insight and practical tools',
          'Clients who feel stuck despite real effort',
          'Spanish-speaking clients seeking care in their primary language',
        ],
      },
      {
        title: 'What therapy with Michelle feels like',
        theme: 'dark',
        paragraphs: [
          'Therapy with Michelle is warm, engaged, steady, and collaborative — therapy that feels less intimidating and more human from the start, paced in a way that is sustainable.',
          'Michelle helps clients slow down, feel heard, and build practical tools that support greater clarity, emotional steadiness, and daily wellbeing.',
        ],
      },
      {
        title: 'Language and cultural resonance',
        paragraphs: [
          'Michelle offers bilingual care in English and Spanish, helping clients express themselves more fully and engage in therapy in the language that feels most natural and supportive.',
          'She is especially attuned to clients navigating bicultural identity, family expectations, and the pressure to hold everything together.',
        ],
      },
      {
        title: "Michelle's approach",
        theme: 'dark',
        paragraphs: [
          'Michelle blends person-centered therapy with practical, CBT-informed tools, in a supportive, nonjudgmental space — so you feel heard and understood while also building what helps in daily life.',
        ],
      },
      {
        title: 'Practical details',
        bullets: [
          'MHC-LP, supervised clinician',
          'individuals and couples',
          'bilingual, English and Spanish',
          'online across New York',
          'insurance accepted',
        ],
      },
    ],
    howToGetStartedText:
      'We help match clients based on fit, insurance, preferences, language needs, and current availability.',
    howToGetStartedLinks: [
      { label: 'Insurance & Fees', href: '/insurance-fees' },
      { label: 'Find Your Therapist', href: '/our-clinicians' },
    ],
    finalText:
      'If you are looking for bilingual therapy in New York that feels warm, collaborative, and grounded in both insight and practical support, Michelle may be a strong fit.',
    finalSecondary: { label: 'Work With a Supervised Clinician', href: '/start-therapy-sooner' },
  },
};

export function getClinicianProfile(slug) {
  return clinicianProfiles[slug];
}
